/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useMemo, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { func, shape, string, bool } from 'prop-types'
import { text } from '@groovehq/internal-design-system/lib/styles/elements'
import { Trash } from '@groovehq/internal-design-system/lib/assets/icons'
import Dropdown from '@groovehq/internal-design-system/lib/components/Dropdown/Dropdown'
import Button from '@groovehq/internal-design-system/lib/components/Button/Button'

import { selectAgentsSortedByName } from 'selectors/agents/base'
import { selectGroupsSortedByName } from 'selectors/app/groups'
import { selectMailboxes } from 'selectors/mailboxes_base'
import { reverseHashInt } from 'util/scatterSwap'

import RuleValue from '../RuleValue'
import DropdownMenu from '../DropdownMenu'
import { styles } from './Condition.styles'

const selectValue = (all, key) => {
  const selected = all.find(item => item.value === key) || {
    name: '',
  }
  return selected.name
}

const buildPossibleOptions = (
  values,
  parameterKey,
  agents,
  groups,
  mailboxes,
  { decodeIds }
) => {
  let possibleOptionsArray
  const possibleOptions = values[parameterKey]
  const { valueType, unassigned, anyone, currentUser } = possibleOptions || {}

  if (valueType === 'Group') {
    possibleOptionsArray = []
    if (unassigned) {
      possibleOptionsArray.push({ name: 'Unassigned', value: '0' })
    }
    groups.forEach(m => {
      let id = m.id
      if (decodeIds) {
        id = reverseHashInt(m.id).toString()
      }
      possibleOptionsArray.push({ name: m.name, value: id })
    })
  } else if (valueType === 'Mailbox') {
    possibleOptionsArray = []
    mailboxes.forEach(m => {
      let id = m.id
      if (decodeIds) {
        id = reverseHashInt(m.id).toString()
      }
      possibleOptionsArray.push({ name: m.name, value: id })
    })
  } else if (valueType === 'Agent') {
    possibleOptionsArray = []
    if (anyone) {
      possibleOptionsArray.push({ name: 'Anyone', value: '0' })
    }
    if (currentUser) {
      possibleOptionsArray.push({ name: 'Current User', value: '-1' })
    }
    if (unassigned) {
      possibleOptionsArray.push({ name: 'Unassigned', value: '0' })
    }
    agents.forEach(m => {
      let id = m.id
      if (decodeIds) {
        id = reverseHashInt(m.id).toString()
      }
      possibleOptionsArray.push({ name: m.name, value: id })
    })
  } else {
    possibleOptionsArray = possibleOptions
  }

  return possibleOptionsArray
}

const Condition = ({
  variables,
  onRemoveCondition,
  conditionId,
  conditionParam,
  conditionOperator,
  conditionValue,
  conditionErrorMessage,
  onBlur,
  index,
  onConditionChange,
  runChangeOnBlur,
  decodeIds = false,
}) => {
  const condition = {
    id: conditionId,
    param: conditionParam,
    operator: conditionOperator,
    value: conditionValue,
  }

  const { parameters, param_operators: paramOperators, values } = variables
  const [parameterKey, setParameterKey] = useState(conditionParam)
  const [paramOperatorKey, setParamOperatorKey] = useState(conditionOperator)
  const [valueKey, setValueKey] = useState(conditionValue)
  const [paramOperator, setParamOperator] = useState(
    paramOperators[parameterKey] || []
  )
  const agents = useSelector(selectAgentsSortedByName)
  const groups = useSelector(selectGroupsSortedByName)
  const mailboxes = useSelector(selectMailboxes)
  const dataType = ['labels', 'TAGS'].includes(condition.param)
    ? 'TAGS'
    : 'AUTO'

  const possibleOptionsArray = buildPossibleOptions(
    values,
    parameterKey,
    agents,
    groups,
    mailboxes,
    { decodeIds }
  )
  const [value, setValue] = useState(possibleOptionsArray)

  const selectedParameter = useMemo(
    () => selectValue(parameters, parameterKey),
    [parameterKey, parameters]
  )

  const selectedOperator = useMemo(
    () => selectValue(paramOperator, paramOperatorKey),
    [paramOperator, paramOperatorKey]
  )

  const selectedValue = useMemo(
    () => {
      return Array.isArray(value) ? selectValue(value, valueKey) : null
    },
    [value, valueKey]
  )

  // Handle select parameter
  const handleSelectParameter = useCallback(
    key => {
      setParameterKey(key)
      const newParamOperator = paramOperators[key] || []
      const newValue = values[key] || ''
      let lastValue = newValue[0] ? newValue[0].value : ''
      setParamOperator(newParamOperator)
      setParamOperatorKey(newParamOperator[0] && newParamOperator[0].value)

      const newPossibleOptionsArray = buildPossibleOptions(
        values,
        key,
        agents,
        groups,
        mailboxes,
        { decodeIds }
      )
      setValue(newPossibleOptionsArray)
      if (Array.isArray(newPossibleOptionsArray)) {
        lastValue = newPossibleOptionsArray[0].value
      }
      setValueKey(lastValue)

      // Pass select values to condition

      onConditionChange(
        {
          ...condition,
          param: key,
          operator: newParamOperator[0].value,
          value: lastValue,
        },
        index
      )
    },
    [
      condition,
      onConditionChange,
      paramOperators,
      agents,
      groups,
      mailboxes,
      values,
      index,
      decodeIds,
    ]
  )

  // Handle select param operator
  const handleSelectParamOperatorKey = useCallback(
    key => {
      setParamOperatorKey(key)
      onConditionChange(
        {
          ...condition,
          operator: key,
        },
        index
      )
    },
    [condition, onConditionChange, index]
  )

  // Handle select value
  const handleSelectValueKey = useCallback(
    key => {
      setValueKey(key)
      onConditionChange(
        {
          ...condition,
          value: key,
        },
        index
      )
    },
    [condition, onConditionChange, index]
  )

  const handleRemove = useCallback(
    () => {
      onRemoveCondition(condition, index)
    },
    [condition, onRemoveCondition, index]
  )

  const handleInput = useCallback(
    e => {
      const newValue = e.target.value
      setValueKey(newValue)
      if (!runChangeOnBlur) {
        onConditionChange(
          {
            ...condition,
            value: newValue,
          },
          index
        )
      }
    },
    [condition, onConditionChange, index, runChangeOnBlur]
  )

  const handleBlur = useCallback(
    e => {
      const newValue = e.target.value
      setValueKey(newValue)
      if (runChangeOnBlur) {
        onConditionChange(
          {
            ...condition,
            value: newValue,
          },
          index
        )
      }
      onBlur(e)
    },
    [condition, onConditionChange, onBlur, index, runChangeOnBlur]
  )

  return (
    <div css={styles.base}>
      <div css={styles.dropdowns}>
        <div css={styles.dropdownContainer} title={selectedParameter}>
          <Dropdown
            overlay={<DropdownMenu data={parameters} />}
            onSelect={handleSelectParameter}
          >
            <Dropdown.Button size="small" css={styles.dropdownBtn}>
              {selectedParameter || 'Select a condition...'}
            </Dropdown.Button>
          </Dropdown>
        </div>
        {parameterKey && (
          <div css={styles.dropdownContainer}>
            <Dropdown
              overlay={<DropdownMenu data={paramOperator} />}
              onSelect={handleSelectParamOperatorKey}
            >
              <Dropdown.Button size="small" css={styles.dropdownBtn}>
                {selectedOperator || <>&nbsp;</>}
              </Dropdown.Button>
            </Dropdown>
          </div>
        )}
        {paramOperatorKey && (
          <RuleValue
            value={value}
            onDropdownSelect={handleSelectValueKey}
            selectedValue={selectedValue}
            conditionOrAction={condition}
            onInputBlur={handleBlur}
            onInputChange={handleInput}
            valueKey={valueKey}
            dataType={dataType}
            errorMessage={conditionErrorMessage}
          />
        )}
        <div>
          <Button
            type="tertiary"
            size="small"
            customIcon={<Trash />}
            css={text.styles.textMediumDark}
            onClick={handleRemove}
          />
        </div>
      </div>
    </div>
  )
}

Condition.propTypes = {
  variables: shape({}),
  onRemoveCondition: func,
  onBlur: func,
  onConditionChange: func,
  conditionId: string,
  conditionParam: string,
  conditionOperator: string,
  conditionValue: string,
  conditionErrorMessage: string,
  runChangeOnBlur: bool,
}

Condition.defaultProps = {
  variables: {},
  onRemoveCondition() {},
  onBlur() {},
  onConditionChange() {},
  runChangeOnBlur: false,
  conditionId: null,
  conditionParam: null,
  conditionOperator: null,
  conditionValue: '',
  conditionErrorMessage: null,
}

export default React.memo(Condition)
